import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDocumentByUUID } from "../../../../api/documentsService";
import { updateStatusForTimeline } from "../../../../api/timelineService";
import useCustomToast from "../../../../customHooks/useCustomToast";
import { setDocumentsFromApiCall } from "../../../../Redux/documentsSlice";
import {
  cancelUpload,
  initiateCancelUploadProcess,
  keepUploading,
  retryUpload,
} from "../../../../utils/cancelFileUpload";
import { uploadFileWithProgress } from "../../../../utils/UTILITY_METHODS";
import FileUpload from "../../../FileUpload";
import CancelFileUploadModal from "../../../Modals/CancelFileUploadModal";
import FillGstDetailsModal from "../../../Modals/FillGstDetailsModal";
import UploadProgress from "../../../UploadProgress";
import SingleGstDocument from "./SingleGstDocument";

const GstDocuments = ({ handleUserTabClicked }) => {
  const { gstInformation } = useSelector((store) => store.documentsDetails);
  const [gstUploadSuccess, setGstUploadSuccess] = useState(false);
  const [gstUploadProgress, setGstUploadProgress] = useState(0);
  const [isGstUploading, setIsGstUploading] = useState(false);
  const [gstUploadFailed, setGstUploadFailed] = useState(false);
  const [gstDetailFile, setGstDetailFile] = useState(null);
  const [lastGstFile, setLastGstFile] = useState(null);
  const [formValues, setFormValues] = useState({
    gstNumber: "",
  });
  const showToast = useCustomToast();
  const dispatch = useDispatch();

  // Create a ref to store the AbortController
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  // State for the  cancel file upload confirmation modal
  const {
    isOpen: isCancelFileUploadModalOpen,
    onOpen: openCancelFileUploadModal,
    onClose: closeCancelFileUploadModal,
  } = useDisclosure();

  //state to open modal
  const {
    isOpen: isGstDetailsModalOpen,
    onOpen: openGstDetailsModal,
    onClose: closeGstDetailsModal,
  } = useDisclosure();

  const handleGstUpload = async (files) => {
    const file = files[0];
    setLastGstFile(file); // Store file for retry
    setGstUploadFailed(false); // Reset failure state
    setIsGstUploading(true); // Show uploading state

    // Initialize a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = await uploadFileWithProgress(
        file,
        setGstUploadProgress,
        setGstUploadFailed,
        setGstUploadSuccess,
        {
          documentType: "gst",
          documentSubType: "gst_report",
          documentNumber: formValues.gstNumber, // this is gst value
          documentNumberIdentifier: "gstin",
          level: "customer",
        },
        abortController.signal // Pass the signal
      );
      setIsGstUploading(false);
      setGstUploadProgress(0);
      setFormValues({
        gstDetailFile,
      });
      showToast(data.message, "success");
      if (isCancelFileUploadModalOpen) closeCancelFileUploadModal();
      const gstDocumentUUID = data.data;
      const DocumentDetails = await getDocumentByUUID(gstDocumentUUID);
      dispatch(setDocumentsFromApiCall([DocumentDetails.data]));
      abortControllerRef.current = null; // Reset the controller
    } catch (error) {
      if (error.message === "Upload canceled") {
        showToast("Upload canceled", "warning");
      } else {
        showToast(error.message || "An error occurred during upload", "error");
      }
      setIsGstUploading(false);
      abortControllerRef.current = null; // Reset the controller
    }
  };

  // Function to trigger the hidden input click
  const handleInputClick = () => {
    inputRef.current.click(); // This triggers the hidden input element
  };

  // Handle form submission (GST Details Modal)
  const handleFormSubmit = () => {
    if (formValues.gstNumber.trim()) {
      closeGstDetailsModal(); // Close the modal after successful submission
      if (gstDetailFile) {
        const file = gstDetailFile;
        handleGstUpload(file);
        setGstDetailFile(null);
      } else {
        handleInputClick();
      }
    } else {
      showToast("All fields are required.", "warning");
    }
  };

  // Reset modal and form state on cancel
  const handleGstDetailsModalCancel = () => {
    setFormValues({ gstNumber: "" });
    setGstUploadProgress(0);
    closeGstDetailsModal();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      minHeight="100px" // Ensure there's a minimum height to avoid content squishing
      maxHeight="auto" // Allow the component to grow as necessary
    >
      <Box>
        <Heading as="h2" size="md">
          GST Information
        </Heading>
        <Text color={"gray.600"} fontSize="sm" mt={2}>
          Please connect to your GST portal to automatically fetch your GST
          filings. This ensures we have accurate sales and tax.
        </Text>

        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={2}
          mt={5}
          onClick={() => {
            openGstDetailsModal();
          }}
        >
          <FileUpload
            onFileUpload={handleGstUpload}
            width={"100%"}
            isUploading={isGstUploading}
            uploadFailed={gstUploadFailed}
            uploadSuccess={gstUploadSuccess}
            isIdentificationDocument={false}
            inputRef={inputRef}
            setFillDetailsModalOpen={openGstDetailsModal}
            setFileToUpload={setGstDetailFile}
            acceptImageOrPdf={false}
          />
        </Stack>

        <FillGstDetailsModal
          isGstDetailsModalOpen={isGstDetailsModalOpen}
          closeGstDetailsModal={closeGstDetailsModal}
          setFormValues={setFormValues}
          formValues={formValues}
          handleFormSubmit={handleFormSubmit}
          handleGstDetailsModalCancel={handleGstDetailsModalCancel}
        />

        {/* GST Document Upload Progress */}
        {isGstUploading && (
          <UploadProgress
            documentType="GST Document"
            isUploading={isGstUploading}
            uploadProgress={gstUploadProgress}
            uploadFailed={gstUploadFailed}
            initiateCancelUploadProcess={() =>
              initiateCancelUploadProcess(
                abortControllerRef,
                openCancelFileUploadModal
              )
            }
            onRetry={() =>
              retryUpload(gstUploadFailed, lastGstFile, handleGstUpload)
            }
          />
        )}
      </Box>

      {/* Cancel Upload Confirmation Modal */}
      <CancelFileUploadModal
        isModalOpen={isCancelFileUploadModalOpen}
        closeModal={closeCancelFileUploadModal}
        cancelUpload={() =>
          cancelUpload(
            abortControllerRef,
            setIsGstUploading,
            setGstUploadProgress,
            closeCancelFileUploadModal
          )
        }
        keepUploading={() => keepUploading(closeCancelFileUploadModal)}
      />

      <Stack spacing={3} mt={5}>
        {gstInformation.map((document, index) => (
          <SingleGstDocument
            key={document.uuid || index}
            name={
              document?.original_filename.length > 20
                ? `${document?.original_filename.substring(0, 20)}...`
                : document?.original_filename
            }
            isLast={index === gstInformation.length - 1}
            scannedCopyDetected={index === 2}
            detectedMessage={"Scanned Copy Detected"}
          />
        ))}
      </Stack>

      <Box display={"flex"} justifyContent={"flex-end"} columnGap={4} mt={5}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          size="sm"
          variant="outline"
          px={12}
          onClick={() => handleUserTabClicked(2)}
        >
          Back
        </Button>
        <Button
          colorScheme={!Boolean(gstInformation.length) ? "blackAlpha" : "teal"}
          color={!Boolean(gstInformation.length) ? "blackAlpha" : "white"}
          size="sm"
          variant="solid"
          px={12}
          disabled={!Boolean(gstInformation.length)}
          onClick={() => {
            handleUserTabClicked(4);
            //update user timeline on server
            updateStatusForTimeline(
              5,
              "financial_information",
              "gst_information",
              "completed"
            );
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default GstDocuments;
